
// Реакт
import React, { useState, useLayoutEffect, useContext, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
// API
import ApiAnalyticV2 from "../../../../../../app/api/authorized/admin/statistic/ApiBookingAnalyticV2";
// Компоненты
import Page from "../../../../../modules/page";
import { ContextPage } from "../../../../../modules/page/pageContext";
import { ContextContent } from "../../../../../modules/content/contentContext";
import Filters from "../../../../../modules/filters";
import MultiSelect from "../../../../../components/inputs/milti-select";
import InputDate from "../../../../../components/inputs/date";
import InputText from "../../../../../components/inputs/text/text";
// Стили
import './styles.css';

const debounce = (func, delay) => {
    let timer;
    return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func.apply(this, args), delay);
    };
};

export default function AnalyticBookingIndex({ ...other }) {
    return (
        <Page {...other}>
            <AnalyticBookingIndexContent {...other} />
        </Page>
    );
}

function AnalyticBookingIndexContent({ ...other }) {
    const contextContent = useContext(ContextContent);
    const contextPage = useContext(ContextPage);

    useLayoutEffect(() => {
        contextPage.setTitle(`Аналитика (бронирования)`);
        contextContent.setTitle(`Аналитика (бронирования)`);
    }, [contextContent, contextPage]);

    const [_dateStart, set_dateStart] = useState(null);
    const [_dateEnd, set_dateEnd] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [_orderTypesIds, set_orderTypes] = useState([]);
    const [_paymentStatusIds, set_paymentStatusIds] = useState([]);
    const [_comment, set_comment] = useState('');
    const [debouncedComment, setDebouncedComment] = useState('');

    const bookingBalanceTypes = useSelector(state => state.adminOption.bookingBalanceTypes);
    const orderTypes = useSelector(state => state.commonOption.bookingTypes);
    const paymentStatuses = useSelector(state => state.commonOption.bookingStatuses);

    const handlePaymentStatusChange = (event) => {
        set_paymentStatusIds(event.target.value);
    }

    const handleOrderTypesChange = (event) => {
        set_orderTypes(event.target.value);
    }

    // Обработчик с debounce
    const handleCommentChange = useCallback(
        debounce((value) => {
            setDebouncedComment(value);
        }, 500), // Задержка 500 мс
        []
    );

    // Обработчик изменения поля ввода
    const onCommentChange = (e) => {
        const value = e.target.value;
        set_comment(value); // Мгновенное обновление UI
        handleCommentChange(value); // Отложенное обновление для запроса
    };

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const apiIndex = ApiAnalyticV2.Index({
                    date_start: _dateStart,
                    date_start_right: _dateEnd,
                    types: _orderTypesIds,
                    statuses: _paymentStatusIds,
                    comment: debouncedComment
                });
                const responseData = await apiIndex.getResponse();

                if (responseData && typeof responseData === 'object') {
                    setTableData(responseData);
                } else {
                    setTableData([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                setTableData([]);
            } finally {
                setIsLoading(false);
            }
        };

        if (_dateStart && _dateEnd) {
            fetchData();
        }
    }, [_dateStart, _dateEnd, _orderTypesIds, _paymentStatusIds, debouncedComment]);

    const Thead = () => {
        return (
            <thead>
                <tr>
                    <th rowSpan="3">Дата</th>
                    <th colSpan="2" rowSpan="2">Итого</th>
                    <th colSpan={Object.keys(orderTypes).length * 2}>Тип бронирования</th>
                    <th colSpan={Object.keys(bookingBalanceTypes).length * 2}>Депозиты</th>
                </tr>
                <tr>
                    {Object.values(orderTypes).map(type => (
                        <th colSpan="2" key={type}>
                            {type === "Бронирование" ? "Оплата" : type}
                        </th>
                    ))}
                    {Object.values(bookingBalanceTypes).map(type => (
                        <th colSpan="2" key={type}>
                            {type === "Перворазное время" ? "Тарифы для новичков" : type}
                        </th>
                    ))}
                </tr>
                <tr>
                    <th>Минут</th>
                    <th>Кол-во</th>
                    {Object.values(orderTypes).map(type => (
                        <React.Fragment key={type}>
                            <th>Минут</th>
                            <th>Кол-во</th>
                        </React.Fragment>
                    ))}
                    {Object.values(bookingBalanceTypes).map(type => (
                        <React.Fragment key={type}>
                            <th>Минут</th>
                            <th>Кол-во</th>
                        </React.Fragment>
                    ))}
                </tr>
            </thead>
        )
    };

    const renderRow = (date, rowData) => {
        const hasData = !Array.isArray(rowData.total);
        const formattedDate = formatDisplayDate(date);

        return (
            <tr key={date}>
                <td>{formattedDate}</td>
                <td>{hasData ? rowData.total?.minutes || 0 : 0}</td>
                <td>{hasData ? rowData.total?.count || 0 : 0}</td>
                {Object.entries(orderTypes).map(([typeId, typeName]) => {
                    const data = hasData ? rowData.types?.[typeId] : null;
                    return (
                        <React.Fragment key={typeId}>
                            <td>{data?.minutes || 0}</td>
                            <td>{data?.count || 0}</td>
                        </React.Fragment>
                    );
                })}
                {Object.entries(bookingBalanceTypes).map(([typeId, typeName]) => {
                    const data = hasData ? rowData.balance_types?.[typeId] : null;
                    return (
                        <React.Fragment key={typeId}>
                            <td>{data?.minutes || 0}</td>
                            <td>{data?.count || 0}</td>
                        </React.Fragment>
                    );
                })}
            </tr>
        );
    };

    const formatDisplayDate = (dateString) => {
        const date = new Date(dateString);
        return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
    };

    const filters = [
        <InputDate
            value={_dateStart}
            className="input_wf"
            onChange={(e) => set_dateStart(e.target.value)}
            type="date"
        >Дата от</InputDate>,
        <InputDate
            value={_dateEnd}
            className="input_wf"
            onChange={(e) => set_dateEnd(e.target.value)}
            type="date"
        >Дата до</InputDate>,
        <MultiSelect
            options={paymentStatuses}
            className="input_wf option-border-none"
            value={_paymentStatusIds}
            onChange={handlePaymentStatusChange}
        >Статус бронирования</MultiSelect>,
        <MultiSelect
            options={orderTypes}
            className="input_wf option-border-none"
            value={_orderTypesIds}
            onChange={handleOrderTypesChange}
        >Тип оплаты</MultiSelect>,
        <InputText className="input_wf" value={_comment} onChange={onCommentChange}>Комментарий</InputText>,
    ];

    return (
        <>
            <Filters filters={filters}></Filters>

            <div className="table-container">
                <table className="analytics-table">
                    <Thead />
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="100%" style={{ textAlign: 'center' }}>
                                    Загрузка...
                                </td>
                            </tr>
                        ) : (
                            tableData && Object.entries(tableData).map(([date, rowData]) => (
                                renderRow(date, rowData)
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}